import type {
  CreatePrinterRequest,
  PrintLayoutConfig,
  PrinterConnectionType,
  PrinterPurpose,
  PrinterType,
  StorePrinter,
  UpdatePrinterRequest,
} from "@medicorepay/shared/types";
import {
  GOLDEN_INVOICE_PRINT_DOCUMENT,
  defaultInvoiceLayout,
  invoiceFormatForPrinterType,
  renderInvoiceHtmlArtifact,
  resolveValidStoreId,
} from "@medicorepay/shared";
import {
  CONNECTION_TYPE_LABELS,
  PRINTER_PAPER_DIMENSIONS,
  PRINTER_PURPOSE_LABELS,
  PRINTER_TYPE_LABELS,
} from "@medicorepay/shared/types";
import {
  ChevronLeft,
  Copy,
  Edit2,
  Eye,
  Plus,
  Printer,
  Settings2,
  Star,
  TestTube2,
  ToggleLeft,
  ToggleRight,
  Trash2,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { showToast } from "../../lib/toast";
import { productRevivalApi } from "../../services/product-revival-client";
import { printerService } from "../../services/printer.service";
import { useAuthStore } from "../../stores/authStore";

// ─── Printer Type Icon Colors ────────────────────────────────────────────
const PRINTER_TYPE_COLORS: Record<string, string> = {
  THERMAL_58MM: "bg-orange-100 text-orange-700",
  THERMAL_80MM: "bg-blue-100 text-blue-700",
  DOT_MATRIX: "bg-purple-100 text-purple-700",
  DOT_MATRIX_9_5X5_5: "bg-violet-100 text-violet-700",
  LASER_A4: "bg-green-100 text-green-700",
  LASER_A5: "bg-teal-100 text-teal-700",
};

const DEFAULT_LAYOUT: PrintLayoutConfig =
  defaultInvoiceLayout("DOT_MATRIX_9_5X5_5");

// ─── Main Page Component ─────────────────────────────────────────────────
export function PrinterSettingsPage() {
  const navigate = useNavigate();
  const selectedStoreId = useAuthStore((state) =>
    resolveValidStoreId(
      state.selectedStoreId,
      state.user?.storeId,
      state.user?.stores,
    ),
  );

  const [printers, setPrinters] = useState<StorePrinter[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPrinter, setEditingPrinter] = useState<StorePrinter | null>(
    null,
  );
  const [showPreview, setShowPreview] = useState<string | null>(null);
  const [testingPrinterId, setTestingPrinterId] = useState<string | null>(null);

  const loadPrinters = useCallback(async () => {
    if (!selectedStoreId) return;
    try {
      setLoading(true);
      const data = await printerService.getByStore(selectedStoreId);
      setPrinters(data);
    } catch (err: any) {
      showToast.error(err.response?.data?.message || "Failed to load printers");
    } finally {
      setLoading(false);
    }
  }, [selectedStoreId]);

  useEffect(() => {
    loadPrinters();
  }, [loadPrinters]);

  const handleDelete = async (printer: StorePrinter) => {
    if (
      !confirm(
        `Delete printer "${printer.name}"? This action cannot be undone.`,
      )
    )
      return;
    try {
      await printerService.remove(printer.id);
      showToast.success("Printer deleted");
      loadPrinters();
    } catch (err: any) {
      showToast.error(err.response?.data?.message || "Failed to delete");
    }
  };

  const handleToggleActive = async (printer: StorePrinter) => {
    try {
      await printerService.update(printer.id, {
        isActive: !printer.isActive,
      });
      showToast.success(
        printer.isActive ? "Printer disabled" : "Printer enabled",
      );
      loadPrinters();
    } catch (err: any) {
      showToast.error(err.response?.data?.message || "Failed to update");
    }
  };

  const handleSetDefault = async (printer: StorePrinter) => {
    try {
      await printerService.update(printer.id, { isDefault: true });
      showToast.success(`"${printer.name}" set as default`);
      loadPrinters();
    } catch (err: any) {
      showToast.error(err.response?.data?.message || "Failed to update");
    }
  };

  const handleFormSave = () => {
    setShowForm(false);
    setEditingPrinter(null);
    loadPrinters();
  };

  const handleTest = async (printer: StorePrinter) => {
    if (printer.connectionType !== "SYSTEM") {
      showToast.error(
        "Network and serial printers must be tested from the desktop app",
      );
      return;
    }
    const testReference = `web-system-${crypto.randomUUID()}`;
    const testedAt = new Date().toISOString();
    setTestingPrinterId(printer.id);
    try {
      const popup = window.open("", "_blank", "width=600,height=500");
      if (!popup) throw new Error("Allow the printer test window and retry");
      const pageSize =
        printer.printerType === "LASER_A5"
          ? "A5"
          : printer.printerType === "LASER_A4"
            ? "A4"
            : printer.paperHeight
              ? `${printer.paperWidth}mm ${printer.paperHeight}mm`
              : `${printer.paperWidth}mm auto`;
      const name = document.createElement("div");
      name.textContent = printer.name;
      popup.document.write(
        `<!doctype html><html><head><meta charset="utf-8"><title>Printer test</title><style>@page{size:${pageSize};margin:10mm}body{font:14px monospace;text-align:center}h1{margin-top:35mm}</style></head><body><h1>MEDICOREPAY PRINTER TEST</h1><p>${name.innerHTML}</p><p>${new Date().toISOString()}</p></body></html>`,
      );
      popup.document.close();
      popup.focus();
      popup.print();
      if (!window.confirm("Did the printer test page print correctly?")) {
        throw new Error(
          "Printer test was not confirmed; readiness was not recorded",
        );
      }
      await productRevivalApi.confirmPrinterTest(
        printer.id,
        { testReference, testedAt, status: "SUCCEEDED" },
        {
          idempotencyKey: crypto.randomUUID(),
          clientApplication: "web",
        },
      );
      showToast.success(`Test page printed on "${printer.name}"`);
      await loadPrinters();
    } catch (error) {
      try {
        await productRevivalApi.confirmPrinterTest(
          printer.id,
          {
            testReference,
            testedAt,
            status: "FAILED",
            failureMessage:
              error instanceof Error ? error.message : "Printer test failed",
          },
          {
            idempotencyKey: crypto.randomUUID(),
            clientApplication: "web",
          },
        );
        await loadPrinters();
      } catch {
        // Keep the original print failure as the useful operator message.
      }
      showToast.error(
        error instanceof Error ? error.message : "Printer test failed",
      );
    } finally {
      setTestingPrinterId(null);
    }
  };

  if (!selectedStoreId) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-yellow-800">
          Please select a store first to configure printers.
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/settings")}
            className="p-2 hover:bg-gray-100 rounded-lg transition"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Printer Configuration
            </h1>
            <p className="text-sm text-gray-500">
              Configure printers for billing, labels, and reports
            </p>
          </div>
        </div>
        <button
          onClick={() => {
            setEditingPrinter(null);
            setShowForm(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-medium"
        >
          <Plus className="w-4 h-4" />
          Add Printer
        </button>
      </div>

      {/* Printer Cards */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">
          Loading printers...
        </div>
      ) : printers.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
          <Printer className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-600 mb-2">
            No printers configured
          </h3>
          <p className="text-sm text-gray-400 mb-6">
            Add your first printer to start printing invoices, labels, and
            reports.
          </p>
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
          >
            <Plus className="w-4 h-4" />
            Add Printer
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {printers.map((printer) => (
            <PrinterCard
              key={printer.id}
              printer={printer}
              onEdit={() => {
                setEditingPrinter(printer);
                setShowForm(true);
              }}
              onDelete={() => handleDelete(printer)}
              onToggleActive={() => handleToggleActive(printer)}
              onSetDefault={() => handleSetDefault(printer)}
              onTest={() => void handleTest(printer)}
              testing={testingPrinterId === printer.id}
              onPreview={() =>
                setShowPreview(showPreview === printer.id ? null : printer.id)
              }
              showPreview={showPreview === printer.id}
            />
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showForm && (
        <PrinterFormModal
          printer={editingPrinter}
          storeId={selectedStoreId}
          onClose={() => {
            setShowForm(false);
            setEditingPrinter(null);
          }}
          onSave={handleFormSave}
        />
      )}
    </div>
  );
}

// ─── Printer Card ────────────────────────────────────────────────────────
function PrinterCard({
  printer,
  onEdit,
  onDelete,
  onToggleActive,
  onSetDefault,
  onTest,
  testing,
  onPreview,
  showPreview,
}: {
  printer: StorePrinter;
  onEdit: () => void;
  onDelete: () => void;
  onToggleActive: () => void;
  onSetDefault: () => void;
  onTest: () => void;
  testing: boolean;
  onPreview: () => void;
  showPreview: boolean;
}) {
  const layout = printer.layoutConfig
    ? ({
        ...defaultInvoiceLayout(
          invoiceFormatForPrinterType(printer.printerType),
        ),
        ...printer.layoutConfig,
      } as PrintLayoutConfig)
    : null;
  const colorClass =
    PRINTER_TYPE_COLORS[printer.printerType] || "bg-gray-100 text-gray-700";

  return (
    <div
      className={`bg-white rounded-xl border ${printer.isActive ? "border-gray-200" : "border-gray-100 opacity-60"} overflow-hidden`}
    >
      <div className="p-4">
        {/* Header Row */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${colorClass}`}>
              <Printer className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-gray-900">{printer.name}</h3>
                {printer.isDefault && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs font-medium rounded-full">
                    <Star className="w-3 h-3" /> Default
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500">
                {PRINTER_TYPE_LABELS[printer.printerType as PrinterType]} •{" "}
                {PRINTER_PURPOSE_LABELS[printer.purpose as PrinterPurpose]}
              </p>
            </div>
          </div>
        </div>

        {/* Info Pills */}
        <div className="flex flex-wrap gap-2 mb-3">
          <span className="inline-flex items-center px-2 py-1 bg-gray-50 text-gray-600 text-xs rounded-md">
            📄 {printer.paperWidth}mm
            {printer.paperHeight ? ` × ${printer.paperHeight}mm` : " (cont.)"}
          </span>
          <span className="inline-flex items-center px-2 py-1 bg-gray-50 text-gray-600 text-xs rounded-md">
            🔌{" "}
            {
              CONNECTION_TYPE_LABELS[
                printer.connectionType as PrinterConnectionType
              ]
            }
          </span>
          {layout?.compactMode && (
            <span className="inline-flex items-center px-2 py-1 bg-blue-50 text-blue-600 text-xs rounded-md">
              ⚡ Compact
            </span>
          )}
          {layout?.fontSize && (
            <span className="inline-flex items-center px-2 py-1 bg-gray-50 text-gray-600 text-xs rounded-md">
              🔤 {layout.fontSize}pt
            </span>
          )}
          {printer.lastTestStatus === "SUCCEEDED" &&
            printer.lastTestedAt &&
            Date.now() - new Date(printer.lastTestedAt).getTime() <=
              30 * 24 * 60 * 60 * 1_000 && (
              <span className="inline-flex items-center px-2 py-1 bg-green-50 text-green-700 text-xs rounded-md">
                ✓ Tested {printer.lastTestedAt.slice(0, 10)}
              </span>
            )}
          {printer.lastTestStatus === "FAILED" && (
            <span className="inline-flex items-center px-2 py-1 bg-red-50 text-red-700 text-xs rounded-md">
              Test failed — retry required
            </span>
          )}
          {printer.lastTestStatus === "SUCCEEDED" &&
            printer.lastTestedAt &&
            Date.now() - new Date(printer.lastTestedAt).getTime() >
              30 * 24 * 60 * 60 * 1_000 && (
              <span className="inline-flex items-center px-2 py-1 bg-amber-50 text-amber-700 text-xs rounded-md">
                Test expired — retest
              </span>
            )}
        </div>

        {/* Toggle sections */}
        {layout && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {[
              { key: "showStoreIdentity", label: "Shop" },
              { key: "showStoreContact", label: "Contact" },
              { key: "showStoreLicenses", label: "Licences" },
              { key: "showGST", label: "GST" },
              { key: "showHSN", label: "HSN" },
              { key: "showBatch", label: "Batch" },
              { key: "showMRP", label: "MRP" },
              { key: "showDoctor", label: "Doctor" },
              { key: "showMfr", label: "Mfr" },
              { key: "showSKU", label: "SKU" },
              { key: "showSchedule", label: "Sch" },
              { key: "showKitIdentity", label: "Kit" },
              { key: "showKitComponents", label: "Parts" },
              { key: "showChargeDetails", label: "Charge code" },
              { key: "showTenders", label: "Tenders" },
              { key: "showExpiry", label: "Exp" },
              { key: "showTaxBreakdown", label: "Tax" },
            ].map(({ key, label }) => (
              <span
                key={key}
                className={`text-[10px] px-1.5 py-0.5 rounded ${
                  (layout as any)[key]
                    ? "bg-green-50 text-green-700"
                    : "bg-red-50 text-red-400 line-through"
                }`}
              >
                {label}
              </span>
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <div className="flex items-center gap-1">
            <button
              onClick={onEdit}
              className="p-1.5 hover:bg-gray-100 rounded text-gray-500 hover:text-blue-600 transition"
              title="Edit"
            >
              <Edit2 className="w-4 h-4" />
            </button>
            <button
              onClick={onPreview}
              className="p-1.5 hover:bg-gray-100 rounded text-gray-500 hover:text-purple-600 transition"
              title="Preview Layout"
            >
              <Eye className="w-4 h-4" />
            </button>
            <button
              onClick={onTest}
              disabled={
                testing ||
                !printer.isActive ||
                printer.connectionType !== "SYSTEM"
              }
              className="p-1.5 hover:bg-gray-100 rounded text-gray-500 hover:text-green-600 transition disabled:opacity-40"
              title={
                printer.connectionType === "SYSTEM"
                  ? "Print and verify test page"
                  : "Test this direct printer from the desktop app"
              }
            >
              <TestTube2
                className={`w-4 h-4 ${testing ? "animate-pulse" : ""}`}
              />
            </button>
            {!printer.isDefault && (
              <button
                onClick={onSetDefault}
                className="p-1.5 hover:bg-gray-100 rounded text-gray-500 hover:text-yellow-600 transition"
                title="Set as Default"
              >
                <Star className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={onDelete}
              className="p-1.5 hover:bg-gray-100 rounded text-gray-500 hover:text-red-600 transition"
              title="Delete"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
          <button
            onClick={onToggleActive}
            className="flex items-center gap-1.5 text-xs text-gray-500"
            title={printer.isActive ? "Disable printer" : "Enable printer"}
          >
            {printer.isActive ? (
              <ToggleRight className="w-5 h-5 text-green-500" />
            ) : (
              <ToggleLeft className="w-5 h-5 text-gray-400" />
            )}
            {printer.isActive ? "Active" : "Disabled"}
          </button>
        </div>
      </div>

      {/* Preview Panel */}
      {showPreview && (
        <div className="border-t border-gray-100 bg-gray-50 p-4">
          <PrintLayoutPreview printer={printer} />
        </div>
      )}
    </div>
  );
}

// ─── Print Layout Preview ────────────────────────────────────────────────
function PrintLayoutPreview({ printer }: { printer: StorePrinter }) {
  const layout = {
    ...defaultInvoiceLayout(invoiceFormatForPrinterType(printer.printerType)),
    ...(printer.layoutConfig ?? {}),
  } as PrintLayoutConfig;
  const isThermal =
    printer.printerType === "THERMAL_58MM" ||
    printer.printerType === "THERMAL_80MM";
  const isFanfold = printer.printerType === "DOT_MATRIX_9_5X5_5";
  const width = isThermal
    ? printer.paperWidth === 58
      ? 200
      : 280
    : isFanfold
      ? 560
      : 380;
  const previewHtml = useMemo(
    () =>
      renderInvoiceHtmlArtifact(
        GOLDEN_INVOICE_PRINT_DOCUMENT,
        invoiceFormatForPrinterType(printer.printerType),
        layout,
      ),
    [layout, printer.printerType],
  );

  return (
    <div>
      <p className="text-xs font-medium text-gray-600 mb-2 flex items-center gap-1">
        <Eye className="w-3.5 h-3.5" /> Layout Preview
      </p>
      <iframe
        title={`${printer.name} canonical invoice preview`}
        srcDoc={previewHtml}
        sandbox="allow-same-origin"
        className="mx-auto h-96 max-w-full rounded-md border border-gray-200 bg-white shadow-sm"
        style={{
          width: `${width}px`,
          height: isFanfold ? `${(width * 5.5) / 9.5}px` : undefined,
        }}
      />
      <div
        className="hidden"
        style={{
          width: `${width}px`,
          fontFamily: layout.fontFamily,
          fontSize: `${Math.max(layout.fontSize - 2, 6)}px`,
          lineHeight: layout.lineSpacing,
          padding: `${layout.margins.top}px ${layout.margins.right}px`,
        }}
      >
        {/* Header */}
        {layout.showHeader && (
          <div
            className={`text-center border-b border-dashed border-gray-300 pb-1 mb-1 ${isThermal ? "text-[9px]" : "text-[11px]"}`}
          >
            <div className="font-bold">STORE NAME</div>
            <div className="text-gray-500">Store Address, City</div>
            {layout.showGST && (
              <div className="text-gray-500">GSTIN: 29XXXXX | DL: XX-XXXX</div>
            )}
          </div>
        )}

        {/* Invoice Info */}
        <div
          className={`flex justify-between border-b border-dashed border-gray-300 py-0.5 mb-1 ${isThermal ? "text-[8px]" : "text-[10px]"}`}
        >
          <span>Inv# INV-001</span>
          <span>01/01/2026</span>
        </div>

        {layout.showPatientInfo && (
          <div
            className={`border-b border-dashed border-gray-300 pb-0.5 mb-1 ${isThermal ? "text-[8px]" : "text-[10px]"}`}
          >
            <span>Patient: John Doe</span>
            {layout.showDoctor && (
              <span className="ml-2 text-gray-500">Dr. Smith</span>
            )}
          </div>
        )}

        {/* Item Table */}
        <table
          className={`w-full border-collapse ${isThermal ? "text-[7px]" : "text-[9px]"}`}
        >
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-0.5">Item</th>
              {layout.showBatch && <th className="text-left">Bat</th>}
              {layout.showHSN && !isThermal && (
                <th className="text-left">HSN</th>
              )}
              <th className="text-right">Qty</th>
              {layout.showMRP && <th className="text-right">MRP</th>}
              <th className="text-right">Amt</th>
            </tr>
          </thead>
          <tbody>
            {[1, 2, 3].map((i) => (
              <tr key={i} className="border-b border-dotted border-gray-100">
                <td className="py-0.5">Medicine {i}</td>
                {layout.showBatch && <td>B{i}01</td>}
                {layout.showHSN && !isThermal && <td>3004</td>}
                <td className="text-right">{i * 2}</td>
                {layout.showMRP && <td className="text-right">₹{i * 50}</td>}
                <td className="text-right">₹{i * 90}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totals */}
        <div
          className={`border-t border-dashed border-gray-300 pt-0.5 mt-1 ${isThermal ? "text-[8px]" : "text-[10px]"}`}
        >
          {layout.showTaxBreakdown && (
            <>
              <div className="flex justify-between text-gray-500">
                <span>CGST</span>
                <span>₹12.50</span>
              </div>
              <div className="flex justify-between text-gray-500">
                <span>SGST</span>
                <span>₹12.50</span>
              </div>
            </>
          )}
          <div className="flex justify-between font-bold border-t border-gray-200 pt-0.5 mt-0.5">
            <span>TOTAL</span>
            <span>₹295.00</span>
          </div>
        </div>

        {/* Footer */}
        {layout.showFooter && (
          <div
            className={`text-center text-gray-400 border-t border-dashed border-gray-300 pt-1 mt-1 ${isThermal ? "text-[7px]" : "text-[9px]"}`}
          >
            Thank you! Visit again.
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Add/Edit Printer Form Modal ─────────────────────────────────────────
function PrinterFormModal({
  printer,
  storeId,
  onClose,
  onSave,
}: {
  printer: StorePrinter | null;
  storeId: string;
  onClose: () => void;
  onSave: () => void;
}) {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<"general" | "layout">("general");

  // General form state
  const [name, setName] = useState(printer?.name || "");
  const [printerType, setPrinterType] = useState<string>(
    printer?.printerType || "DOT_MATRIX_9_5X5_5",
  );
  const [connectionType, setConnectionType] = useState<string>(
    printer?.connectionType || "SYSTEM",
  );
  const [purpose, setPurpose] = useState<string>(printer?.purpose || "BILLING");
  const [paperWidth, setPaperWidth] = useState(printer?.paperWidth || 241.3);
  const [paperHeight, setPaperHeight] = useState<number | "">(
    printer?.paperHeight || 139.7,
  );
  const [isDefault, setIsDefault] = useState(printer?.isDefault || false);

  // Connection config
  const [connIp, setConnIp] = useState("");
  const [connPort, setConnPort] = useState(9100);
  const [connSerialPort, setConnSerialPort] = useState("");
  const [connBaudRate, setConnBaudRate] = useState(9600);

  // Layout config
  const [layout, setLayout] = useState<PrintLayoutConfig>(
    printer
      ? ({
          ...defaultInvoiceLayout(
            invoiceFormatForPrinterType(printer.printerType),
          ),
          ...(printer.layoutConfig ?? {}),
        } as PrintLayoutConfig)
      : { ...DEFAULT_LAYOUT },
  );

  // Auto-set exact physical dimensions when printer type changes.
  useEffect(() => {
    const dimensions = PRINTER_PAPER_DIMENSIONS[printerType as PrinterType] ?? {
      paperWidth: 80,
      paperHeight: null,
    };
    setPaperWidth(dimensions.paperWidth);
    setPaperHeight(dimensions.paperHeight ?? "");
    if (
      !["THERMAL_58MM", "THERMAL_80MM"].includes(printerType) &&
      ["NETWORK", "SERIAL"].includes(connectionType)
    ) {
      setConnectionType("SYSTEM");
    }
  }, [connectionType, printerType]);

  // Load preset when printer type changes (only for new printers)
  const handleLoadPreset = async () => {
    try {
      const preset = await printerService.getPresets(printerType);
      setLayout(preset as PrintLayoutConfig);
      showToast.success("Preset loaded");
    } catch {
      showToast.error("Failed to load preset");
    }
  };

  // Initialize connection config from existing printer
  useEffect(() => {
    if (printer?.connectionConfig) {
      const cfg = printer.connectionConfig as any;
      if (cfg.ip) setConnIp(cfg.ip);
      if (cfg.networkPort) setConnPort(cfg.networkPort);
      if (cfg.port) setConnSerialPort(cfg.port);
      if (cfg.baudRate) setConnBaudRate(cfg.baudRate);
    }
  }, [printer]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      showToast.error("Printer name is required");
      return;
    }
    if (connectionType === "NETWORK" && !connIp.trim()) {
      showToast.error("Network host or IP address is required");
      return;
    }
    if (
      connectionType === "NETWORK" &&
      (!Number.isInteger(connPort) || connPort < 1 || connPort > 65535)
    ) {
      showToast.error("Network port must be between 1 and 65535");
      return;
    }
    if (connectionType === "SERIAL" && !connSerialPort.trim()) {
      showToast.error("Serial / COM port is required");
      return;
    }

    // Build connection config based on type
    let connectionConfig: any = null;
    if (connectionType === "NETWORK") {
      connectionConfig = { ip: connIp, networkPort: connPort };
    } else if (connectionType === "SERIAL") {
      connectionConfig = { port: connSerialPort, baudRate: connBaudRate };
    }

    try {
      setLoading(true);
      if (printer) {
        const data: UpdatePrinterRequest = {
          name,
          printerType: printerType as any,
          connectionType: connectionType as any,
          purpose: purpose as any,
          paperWidth,
          paperHeight: paperHeight || undefined,
          connectionConfig,
          layoutConfig: layout,
          isDefault,
        };
        await printerService.update(printer.id, data);
        showToast.success("Printer updated");
      } else {
        const data: CreatePrinterRequest = {
          storeId,
          name,
          printerType: printerType as any,
          connectionType: connectionType as any,
          purpose: purpose as any,
          paperWidth,
          paperHeight: paperHeight || undefined,
          connectionConfig,
          layoutConfig: layout,
          isDefault,
        };
        await printerService.create(data);
        showToast.success("Printer added");
      }
      onSave();
    } catch (err: any) {
      showToast.error(err.response?.data?.message || "Failed to save printer");
    } finally {
      setLoading(false);
    }
  };

  const updateLayout = (key: keyof PrintLayoutConfig, value: any) => {
    setLayout((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            {printer ? "Edit Printer" : "Add Printer"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl"
          >
            ×
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 px-6">
          <button
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition ${activeTab === "general" ? "border-blue-600 text-blue-600" : "border-transparent text-gray-500 hover:text-gray-700"}`}
            onClick={() => setActiveTab("general")}
          >
            <Settings2 className="w-4 h-4 inline mr-1.5" />
            General
          </button>
          <button
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition ${activeTab === "layout" ? "border-blue-600 text-blue-600" : "border-transparent text-gray-500 hover:text-gray-700"}`}
            onClick={() => setActiveTab("layout")}
          >
            <Eye className="w-4 h-4 inline mr-1.5" />
            Print Layout
          </button>
        </div>

        {/* Form Body */}
        <form
          onSubmit={handleSubmit}
          className="flex-1 overflow-y-auto px-6 py-4"
        >
          {activeTab === "general" && (
            <div className="space-y-4">
              {/* Name */}
              <div>
                <label
                  htmlFor="printer-name"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Printer Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="printer-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Billing Counter 1"
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Printer Type + Purpose (2-col) */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="printer-type"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Printer Type
                  </label>
                  <select
                    id="printer-type"
                    value={printerType}
                    onChange={(e) => setPrinterType(e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {Object.entries(PRINTER_TYPE_LABELS).map(([val, label]) => (
                      <option key={val} value={val}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label
                    htmlFor="printer-purpose"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Purpose
                  </label>
                  <select
                    id="printer-purpose"
                    value={purpose}
                    onChange={(e) => setPurpose(e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {Object.entries(PRINTER_PURPOSE_LABELS).map(
                      ([val, label]) => (
                        <option key={val} value={val}>
                          {label}
                        </option>
                      ),
                    )}
                  </select>
                </div>
              </div>

              {/* Connection + Paper (2-col) */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="printer-connection"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Connection
                  </label>
                  <select
                    id="printer-connection"
                    value={connectionType}
                    onChange={(e) => setConnectionType(e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {connectionType === "USB" && (
                      <option value="USB" disabled>
                        USB Direct (unsupported — select another connection)
                      </option>
                    )}
                    {Object.entries(CONNECTION_TYPE_LABELS)
                      .filter(([val]) => val !== "USB")
                      .map(([val, label]) => (
                        <option key={val} value={val}>
                          {label}
                        </option>
                      ))}
                  </select>
                </div>
                <div>
                  <label
                    htmlFor="printer-paper-width"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Paper Width (mm)
                  </label>
                  <input
                    id="printer-paper-width"
                    type="number"
                    value={paperWidth}
                    onChange={(e) => setPaperWidth(Number(e.target.value))}
                    min={20}
                    max={300}
                    step="0.1"
                    disabled={printerType === "DOT_MATRIX_9_5X5_5"}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {connectionType === "SYSTEM" && (
                <p className="rounded-lg bg-blue-50 px-4 py-3 text-xs text-blue-800">
                  Uses the operating-system print dialog and works in the web
                  app. Network and serial direct printing require MediCorePay
                  Desktop.
                </p>
              )}

              {(printerType === "LASER_A4" ||
                printerType === "LASER_A5" ||
                printerType === "DOT_MATRIX_9_5X5_5") && (
                <div>
                  <label
                    htmlFor="printer-paper-height"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Paper Height (mm)
                  </label>
                  <input
                    id="printer-paper-height"
                    type="number"
                    value={paperHeight}
                    onChange={(e) => setPaperHeight(Number(e.target.value))}
                    min={20}
                    max={400}
                    step="0.1"
                    disabled={printerType === "DOT_MATRIX_9_5X5_5"}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}

              {/* Connection-specific config */}
              {connectionType === "NETWORK" && (
                <div className="grid grid-cols-2 gap-4 bg-gray-50 rounded-lg p-4">
                  <div>
                    <label
                      htmlFor="printer-network-host"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      IP Address
                    </label>
                    <input
                      id="printer-network-host"
                      type="text"
                      value={connIp}
                      onChange={(e) => setConnIp(e.target.value)}
                      placeholder="192.168.1.100"
                      className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="printer-network-port"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Port
                    </label>
                    <input
                      id="printer-network-port"
                      type="number"
                      value={connPort}
                      onChange={(e) => setConnPort(Number(e.target.value))}
                      className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              )}

              {connectionType === "SERIAL" && (
                <div className="grid grid-cols-2 gap-4 bg-gray-50 rounded-lg p-4">
                  <div>
                    <label
                      htmlFor="printer-serial-port"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      COM Port
                    </label>
                    <input
                      id="printer-serial-port"
                      type="text"
                      value={connSerialPort}
                      onChange={(e) => setConnSerialPort(e.target.value)}
                      placeholder="/dev/ttyUSB0 or COM3"
                      className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="printer-baud-rate"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Baud Rate
                    </label>
                    <select
                      id="printer-baud-rate"
                      value={connBaudRate}
                      onChange={(e) => setConnBaudRate(Number(e.target.value))}
                      className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {[9600, 19200, 38400, 57600, 115200].map((rate) => (
                        <option key={rate} value={rate}>
                          {rate}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* Options */}
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={isDefault}
                    onChange={(e) => setIsDefault(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  Set as default for{" "}
                  {PRINTER_PURPOSE_LABELS[purpose as PrinterPurpose] || purpose}
                </label>
              </div>
            </div>
          )}

          {activeTab === "layout" && (
            <div className="space-y-4">
              {/* Preset loader */}
              <div className="flex items-center justify-between bg-blue-50 rounded-lg p-3">
                <span className="text-sm text-blue-700">
                  Load recommended settings for{" "}
                  {PRINTER_TYPE_LABELS[printerType as PrinterType]}
                </span>
                <button
                  type="button"
                  onClick={handleLoadPreset}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-md text-xs font-medium hover:bg-blue-700"
                >
                  <Copy className="w-3.5 h-3.5" />
                  Load Preset
                </button>
              </div>

              {/* Typography */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">
                  Typography
                </h4>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      Font Size
                    </label>
                    <input
                      type="number"
                      value={layout.fontSize}
                      onChange={(e) =>
                        updateLayout("fontSize", Number(e.target.value))
                      }
                      min={6}
                      max={16}
                      className="w-full px-3 py-1.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      Font Family
                    </label>
                    <select
                      value={layout.fontFamily}
                      onChange={(e) =>
                        updateLayout("fontFamily", e.target.value)
                      }
                      className="w-full px-3 py-1.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="monospace">Monospace</option>
                      <option value="sans-serif">Sans-serif</option>
                      <option value="serif">Serif</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      Line Spacing
                    </label>
                    <input
                      type="number"
                      value={layout.lineSpacing}
                      onChange={(e) =>
                        updateLayout("lineSpacing", Number(e.target.value))
                      }
                      min={1}
                      max={2}
                      step={0.1}
                      className="w-full px-3 py-1.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Margins */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">
                  Margins (mm)
                </h4>
                <div className="grid grid-cols-4 gap-2">
                  {(["top", "right", "bottom", "left"] as const).map((side) => (
                    <div key={side}>
                      <label className="block text-xs text-gray-500 mb-1 capitalize">
                        {side}
                      </label>
                      <input
                        type="number"
                        value={layout.margins[side]}
                        onChange={(e) =>
                          updateLayout("margins", {
                            ...layout.margins,
                            [side]: Number(e.target.value),
                          })
                        }
                        min={0}
                        max={50}
                        className="w-full px-3 py-1.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Section Toggles */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">
                  Invoice Sections
                </h4>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    {
                      key: "compactMode",
                      label: "Compact Mode",
                      desc: "Reduce spacing & font size",
                    },
                    {
                      key: "showHeader",
                      label: "Store Header",
                      desc: "Master switch for header blocks",
                    },
                    {
                      key: "showStoreIdentity",
                      label: "Shop Name & Address",
                      desc: "Pharmacy identity block",
                    },
                    {
                      key: "showStoreContact",
                      label: "Contact Details",
                      desc: "Phone, WhatsApp and email",
                    },
                    {
                      key: "showStoreLicenses",
                      label: "License Details",
                      desc: "GSTIN, DL and FSSAI",
                    },
                    {
                      key: "showFooter",
                      label: "Footer",
                      desc: "Thank you message, terms",
                    },
                    {
                      key: "showPatientInfo",
                      label: "Patient Info",
                      desc: "Customer name, phone",
                    },
                    {
                      key: "showDoctor",
                      label: "Doctor Info",
                      desc: "Prescribing doctor",
                    },
                    {
                      key: "showTenders",
                      label: "Tenders",
                      desc: "Payment method breakdown",
                    },
                  ].map(({ key, label, desc }) => (
                    <label
                      key={key}
                      className="flex items-start gap-2 p-2 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition"
                    >
                      <input
                        type="checkbox"
                        checked={(layout as any)[key]}
                        onChange={(e) =>
                          updateLayout(key as any, e.target.checked)
                        }
                        className="w-4 h-4 mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <div>
                        <span className="text-sm text-gray-700">{label}</span>
                        <p className="text-[10px] text-gray-400">{desc}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Column Toggles */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">
                  Item Details & Column Visibility
                </h4>
                <div className="flex flex-wrap gap-2">
                  {[
                    { key: "showGST", label: "GSTIN in Header" },
                    { key: "showHSN", label: "HSN Code" },
                    { key: "showBatch", label: "Batch No" },
                    { key: "showExpiry", label: "Expiry Date" },
                    { key: "showMRP", label: "MRP" },
                    { key: "showMfr", label: "Manufacturer" },
                    { key: "showSKU", label: "SKU" },
                    { key: "showSchedule", label: "Schedule H/H1" },
                    { key: "showKitIdentity", label: "Kit Identity" },
                    { key: "showKitComponents", label: "Kit Components" },
                    { key: "showChargeDetails", label: "Charge Details" },
                    { key: "showQuantity", label: "Quantity" },
                    { key: "showDiscount", label: "Discount" },
                    { key: "showTaxBreakdown", label: "CGST/SGST Split" },
                  ].map(({ key, label }) => (
                    <label
                      key={key}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer transition border ${
                        (layout as any)[key]
                          ? "bg-blue-50 border-blue-200 text-blue-700"
                          : "bg-gray-50 border-gray-200 text-gray-500"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={(layout as any)[key]}
                        onChange={(e) =>
                          updateLayout(key as any, e.target.checked)
                        }
                        className="sr-only"
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </div>

              {/* Custom Footer Text */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">
                  Custom Footer Lines
                </h4>
                <textarea
                  value={(layout.footerLines || []).join("\n")}
                  onChange={(e) =>
                    updateLayout(
                      "footerLines",
                      e.target.value.split("\n").filter(Boolean),
                    )
                  }
                  rows={2}
                  placeholder="One line per row, e.g.: Thank you! Visit again."
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          )}
        </form>

        {/* Footer Actions */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit as any}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
          >
            {loading ? "Saving..." : printer ? "Update Printer" : "Add Printer"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PrinterSettingsPage;
